/**
 * components/settings/SettingsDangerZone.tsx
 *
 * Section Supprimer mon compte des paramètres.
 * Permet à l'utilisateur de soumettre une demande de suppression de compte.
 * La demande est stockée dans `account_deletion_requests` et traitée manuellement par l'admin.
 * Inclut une modale de confirmation nécessitant la ressaisie de l'email pour la sécurité.
 * Updated: suppression du header coloré interne (titre déplacé dans SettingsSection variant="danger"),
 * la card n'expose plus que le body avec texte + bouton d'action.
 * Updated: checkPendingRequest wrapped in try-catch-finally for robust error handling;
 * shows a Spinner while the pending-request check is in progress instead of rendering null.
 * Updated: destructure both { data, error } from maybeSingle(); set requestCheckError=true on
 * failure instead of enabling the CTA when outcome is unknown; reset all state on user change;
 * setCheckingRequest(true) before each fetch so the spinner reappears on user change.
 */

import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Trash2, Clock, RefreshCw } from 'lucide-react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Spinner from '../ui/Spinner';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  userEmail: string;
  loading: boolean;
}

function DeleteAccountModal({ isOpen, onClose, onConfirmed, userEmail, loading }: DeleteAccountModalProps) {
  const [emailInput, setEmailInput] = useState('');
  const emailMatches = userEmail.length > 0 && emailInput.trim().length > 0 && emailInput.trim().toLowerCase() === userEmail.toLowerCase();

  const handleClose = () => {
    setEmailInput('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Supprimer votre compte"
      size="sm"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={onConfirmed}
            disabled={!emailMatches}
            loading={loading}
          >
            Envoyer la demande
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-4">
          <div className="bg-error-100 rounded-xl p-3 flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-error-600" />
          </div>
          <div className="flex-1 pt-0.5">
            <p className="text-sm text-neutral-700 leading-relaxed">
              Cette action est <span className="font-semibold text-neutral-900">irréversible</span>. Toutes vos données
              (formulaires, clients, documents) seront supprimées. Votre demande sera traitée par notre équipe sous <span className="font-medium">48h</span>.
            </p>
          </div>
        </div>

        <div>
          <Input
            label="Confirmez votre adresse email pour continuer"
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            autoComplete="off"
          />
          {emailInput.length > 0 && !emailMatches && (
            <p className="mt-1.5 text-xs text-error-600 font-medium">L'email ne correspond pas à votre compte</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default function SettingsDangerZone() {
  const { user, userProfile } = useAuth();
  const { showToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [checkingRequest, setCheckingRequest] = useState(true);
  const [requestCheckError, setRequestCheckError] = useState(false);

  const checkPendingRequest = useCallback(async (userId: string) => {
    setCheckingRequest(true);
    setRequestCheckError(false);
    try {
      const { data, error } = await supabase
        .from('account_deletion_requests')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'pending')
        .maybeSingle();

      if (error) {
        setRequestCheckError(true);
      } else {
        setHasPendingRequest(!!data);
      }
    } catch {
      setRequestCheckError(true);
    } finally {
      setCheckingRequest(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setHasPendingRequest(false);
      setCheckingRequest(false);
      setRequestCheckError(false);
      return;
    }

    checkPendingRequest(user.id);
  }, [user, checkPendingRequest]);

  const handleConfirm = async () => {
    if (!user) return;
    setSubmitting(true);

    const { error } = await supabase
      .from('account_deletion_requests')
      .insert({ user_id: user.id });

    setSubmitting(false);

    if (error) {
      showToast('Une erreur est survenue. Veuillez réessayer.', 'error');
    } else {
      setIsModalOpen(false);
      setHasPendingRequest(true);
      showToast('Votre demande de suppression a été envoyée. Notre équipe la traitera sous 48h.', 'success');
    }
  };

  return (
    <>
      <div className="border border-error-200 rounded-2xl bg-white px-6 py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-neutral-500">
            La suppression est définitive. Toutes vos données (formulaires, clients, documents) seront supprimées.
          </p>

          {checkingRequest ? (
            <div className="flex-shrink-0 text-neutral-400">
              <Spinner size="sm" />
            </div>
          ) : requestCheckError ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-error-600">Impossible de vérifier le statut.</span>
              <button
                onClick={() => user && checkPendingRequest(user.id)}
                className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Réessayer
              </button>
            </div>
          ) : hasPendingRequest ? (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-warning-50 border border-warning-200 rounded-xl flex-shrink-0">
              <Clock className="w-4 h-4 text-warning-600 flex-shrink-0" />
              <span className="text-sm font-medium text-warning-700 whitespace-nowrap">Demande en cours de traitement</span>
            </div>
          ) : (
            <Button
              variant="danger-outline"
              onClick={() => setIsModalOpen(true)}
              className="flex-shrink-0"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Demander la suppression
            </Button>
          )}
        </div>
      </div>

      <DeleteAccountModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirmed={handleConfirm}
        userEmail={userProfile?.email ?? user?.email ?? ''}
        loading={submitting}
      />
    </>
  );
}
